import { Link } from 'react-router-dom';
import { lazy, Suspense, useState, useEffect } from 'react';
import { Music, Mail, Instagram, Twitter, Facebook, Radio } from 'lucide-react';
import axios from 'axios';

const ShareButton = lazy(() => import('./ShareButton'));

const API = process.env.REACT_APP_BACKEND_URL;

// App Store Icons as SVG components
const GooglePlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const TuneInIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <circle cx="12" cy="12" r="3"/>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/>
    <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
  </svg>
);

const Footer = () => {
  const [logo, setLogo] = useState(null);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/logo`);
        if (response.data?.value) {
          setLogo(response.data.value);
        }
      } catch (error) {
        setLogoError(true);
      }
    };
    fetchLogo();
  }, []);

  return (
    <footer data-testid="main-site-footer" className="border-t border-white/10 bg-black/50 backdrop-blur-xl mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center space-x-3 mb-4" data-testid="footer-logo">
              {logo && !logoError ? (
                <img 
                  src={logo} 
                  alt="Rastyle Republik" 
                  className="h-10 w-auto"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 via-yellow-500 to-red-500 rounded-sm flex items-center justify-center">
                  <span className="text-black font-bold text-lg">RR</span>
                </div>
              )}
              <span className="font-heading font-bold text-xl tracking-tight">RASTYLE REPUBLIK</span>
            </Link>
            <p className="text-sm text-muted-foreground mb-4">
              Your global reggae culture hub. Mixtapes, live radio, events, and community worldwide.
            </p>
            <div className="flex items-center space-x-4 mb-6">
              <Suspense fallback={<span className="block h-9 w-9" aria-hidden="true" />}>
                <ShareButton
                  title="Rastyle Republik"
                  url="/"
                  description="Discover reggae culture, creators, mixes, events and stories on Rastyle Republik."
                  type="platform"
                  contentId="rastyle-republik"
                  compact
                  className="rounded-sm bg-white/5 hover:bg-primary hover:text-black"
                />
              </Suspense>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-9 h-9 flex items-center justify-center rounded-sm bg-white/5 hover:bg-primary hover:text-black transition-colors duration-300"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-twitter"
                className="w-9 h-9 flex items-center justify-center rounded-sm bg-white/5 hover:bg-primary hover:text-black transition-colors duration-300"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-9 h-9 flex items-center justify-center rounded-sm bg-white/5 hover:bg-primary hover:text-black transition-colors duration-300"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
            
            {/* Rastyle Radio App Links */}
            <div className="border-t border-white/10 pt-4">
              <p className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                <Radio className="w-4 h-4" />
                Listen to Rastyle Radio
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href="https://play.google.com/store/apps/details?id=com.rastyle.rastyleradio&hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-google-play"
                  className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-primary hover:text-black rounded-sm transition-colors duration-300 text-sm"
                  title="Get it on Google Play (Also available for Android TV & Car)"
                >
                  <GooglePlayIcon />
                  <span className="hidden sm:inline">Google Play</span>
                </a>
                <a
                  href="https://apps.apple.com/ke/app/rastyle-radio-do-the-reggae/id6446504420"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-app-store"
                  className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-primary hover:text-black rounded-sm transition-colors duration-300 text-sm"
                  title="Download on the App Store"
                >
                  <AppleIcon />
                  <span className="hidden sm:inline">App Store</span>
                </a>
                <a
                  href="https://tunein.com/radio/Rastyle-Radio-s250919/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-tunein"
                  className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-primary hover:text-black rounded-sm transition-colors duration-300 text-sm"
                  title="Listen on TuneIn"
                >
                  <TuneInIcon />
                  <span className="hidden sm:inline">TuneIn</span>
                </a>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Also available for Android TV & Car installations
              </p>
            </div>
          </div>

          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <Link to="/mixtapes" data-testid="footer-mixtapes" className="hover:text-primary transition-colors duration-300">
                  Mixtapes
                </Link>
              </li>
              <li>
                <Link to="/records" data-testid="footer-records" className="hover:text-primary transition-colors duration-300">
                  Rastyle Records
                </Link>
              </li>
              <li>
                <Link to="/events" data-testid="footer-events" className="hover:text-primary transition-colors duration-300">
                  Events
                </Link>
              </li>
              <li>
                <Link to="/radio" data-testid="footer-radio" className="hover:text-primary transition-colors duration-300">
                  Radio
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">Contact</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <a href="mailto:hello@rastyle.co.ke" data-testid="footer-email" className="hover:text-primary transition-colors duration-300">
                  hello@rastyle.co.ke
                </a>
              </li>
              <li>
                <Link to="/join" data-testid="footer-join" className="hover:text-primary transition-colors duration-300">
                  Join the Republik
                </Link>
              </li>
              <li>
                <Link to="/become-creator" className="hover:text-primary transition-colors duration-300">
                  Become a Creator
                </Link>
              </li>
              <li>
                <a href="https://rastyle.shop" target="_blank" rel="noopener noreferrer" data-testid="footer-shop" className="hover:text-primary transition-colors duration-300">
                  Rastyle Shop
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-8 pt-8 pb-20 text-center text-sm text-muted-foreground">
          <p>
            &copy; {new Date().getFullYear()} Rastyle Republik. All rights reserved.
            <span className="mx-2">&bull;</span>
            <span className="text-primary">#FeelTheVibe</span>
            <span className="mx-2">&bull;</span>
            <span className="text-accent">#MyStyleIsRastyle</span>
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-sm">
            <Link to="/legal/cultural-constitution" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-constitution">
              Cultural Constitution
            </Link>
            <span className="text-muted-foreground/50">&bull;</span>
            <Link to="/legal/creator-charter" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-charter">
              Creator Charter
            </Link>
            <span className="text-muted-foreground/50">&bull;</span>
            <Link to="/legal/terms-fans" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-terms-fans">
              Fan Terms
            </Link>
            <span className="text-muted-foreground/50">&bull;</span>
            <Link to="/legal/terms-creators" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-terms-creators">
              Creator Terms
            </Link>
            <span className="text-muted-foreground/50">&bull;</span>
            <Link to="/legal/terms-ticketing" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-terms-ticketing">
              Ticketing Terms
            </Link>
            <span className="text-muted-foreground/50">&bull;</span>
            <Link to="/legal/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-privacy-new">
              Privacy Policy
            </Link>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-3 text-sm">
            <Link to="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-privacy">
              Legacy Privacy
            </Link>
            <span className="text-muted-foreground/50">&bull;</span>
            <Link to="/terms-of-service" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-terms">
              Terms of Service
            </Link>
            <span className="text-muted-foreground/50">&bull;</span>
            <Link to="/returns-policy" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-returns">
              Returns Policy
            </Link>
            <span className="text-muted-foreground/50">&bull;</span>
            <Link to="/data-deletion" className="text-muted-foreground hover:text-primary transition-colors" data-testid="footer-data-deletion">
              Data Deletion
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
